import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useCart } from '../contexts/CartContext';
import {
  ArrowLeft, ArrowRight, Sparkles, Droplets, Sun, Layers, Heart,
  Zap, Palette, Clock, Shield, Star, Leaf, Timer, Cloud, Snowflake,
  ShoppingBag, RotateCcw, ChevronRight, Loader2
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICON_MAP = {
  balance: Layers, droplet: Droplets, sun: Sun, layers: Layers,
  heart: Heart, zap: Zap, palette: Palette, clock: Clock,
  sparkles: Sparkles, droplets: Droplets, shield: Shield,
  timer: Timer, star: Star, leaf: Leaf, cloud: Cloud, snowflake: Snowflake,
};

function QuizIntro({ onStart }) {
  return (
    <div className="text-center space-y-6 max-w-lg mx-auto py-12" data-testid="quiz-intro">
      <div className="w-20 h-20 rounded-full bg-[var(--violet-light)] flex items-center justify-center mx-auto">
        <Sparkles className="h-9 w-9 text-[var(--violet)]" />
      </div>
      <h1 className="text-3xl sm:text-4xl font-bold text-[var(--dark)] leading-tight">
        Descubre tu rutina ideal de K-Beauty
      </h1>
      <p className="text-base text-[var(--dark)]/50 leading-relaxed">
        Responde 5 preguntas rapidas sobre tu piel y te recomendaremos los productos
        perfectos para ti, basados en la ciencia del skincare coreano.
      </p>
      <Button
        size="lg"
        className="bg-[var(--violet)] hover:bg-[var(--violet-dark)] text-white rounded-xl h-14 px-10 text-base font-semibold"
        onClick={onStart}
        data-testid="quiz-start-btn"
      >
        Comenzar Quiz <ArrowRight className="ml-2 h-5 w-5" />
      </Button>
      <p className="text-xs text-[var(--dark)]/30">Solo toma 1 minuto</p>
    </div>
  );
}

function QuizQuestion({ question, totalQuestions, currentIndex, selectedAnswer, onSelect, onNext, onBack }) {
  return (
    <div className="max-w-2xl mx-auto py-8" data-testid={`quiz-question-${question.id}`}>
      {/* Progress */}
      <div className="flex items-center gap-3 mb-8">
        <span className="text-xs font-semibold text-[var(--violet)]">{currentIndex + 1}/{totalQuestions}</span>
        <div className="flex-1 h-1.5 bg-[var(--sage)]/15 rounded-full overflow-hidden">
          <div
            className="h-full bg-[var(--violet)] rounded-full transition-all duration-500"
            style={{ width: `${((currentIndex + 1) / totalQuestions) * 100}%` }}
          />
        </div>
      </div>

      <h2 className="text-xl sm:text-2xl font-bold text-[var(--dark)] mb-6 leading-tight">
        {question.question}
      </h2>

      <div className="space-y-3">
        {question.options.map(opt => {
          const Icon = ICON_MAP[opt.icon] || Sparkles;
          const isSelected = selectedAnswer === opt.id;
          return (
            <button
              key={opt.id}
              onClick={() => onSelect(opt.id)}
              className={`w-full text-left p-4 rounded-xl border-2 transition-all duration-200 flex items-center gap-4 ${
                isSelected
                  ? 'border-[var(--violet)] bg-[var(--violet-light)] shadow-sm'
                  : 'border-[var(--sage)]/20 bg-white hover:border-[var(--violet)]/30 hover:shadow-sm'
              }`}
              data-testid={`quiz-option-${opt.id}`}
            >
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                isSelected ? 'bg-[var(--violet)] text-white' : 'bg-[var(--cream)] text-[var(--dark)]/40'
              }`}>
                <Icon className="h-5 w-5" />
              </div>
              <span className={`text-sm font-medium ${isSelected ? 'text-[var(--violet)]' : 'text-[var(--dark)]/70'}`}>
                {opt.text}
              </span>
            </button>
          );
        })}
      </div>

      <div className="flex justify-between mt-8">
        <Button
          variant="outline"
          className="rounded-xl text-sm border-[var(--sage)]/30"
          onClick={onBack}
          disabled={currentIndex === 0}
          data-testid="quiz-back-btn"
        >
          <ArrowLeft className="mr-2 h-4 w-4" /> Anterior
        </Button>
        <Button
          className="rounded-xl text-sm bg-[var(--violet)] hover:bg-[var(--violet-dark)] text-white px-6"
          onClick={onNext}
          disabled={!selectedAnswer}
          data-testid="quiz-next-btn"
        >
          {currentIndex === totalQuestions - 1 ? 'Ver resultados' : 'Siguiente'} <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

function QuizResults({ profile, recommended, onRetake }) {
  const { addItem } = useCart();

  return (
    <div className="max-w-4xl mx-auto py-8" data-testid="quiz-results">
      {/* Profile Card */}
      <div className="bg-gradient-to-br from-[var(--violet-light)] to-white rounded-2xl p-6 sm:p-8 border border-[var(--violet)]/10 mb-10">
        <div className="flex items-start gap-4 mb-5">
          <div className="w-14 h-14 rounded-2xl bg-[var(--violet)] flex items-center justify-center flex-shrink-0">
            <Sparkles className="h-7 w-7 text-white" />
          </div>
          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-[var(--dark)]">Tu Perfil de Piel</h2>
            <p className="text-sm text-[var(--dark)]/50 mt-1">Basado en tus respuestas, esta es tu rutina personalizada</p>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4 mb-5">
          <div className="bg-white/80 rounded-xl p-4">
            <p className="text-[10px] font-semibold text-[var(--violet)] uppercase tracking-wider mb-1">Tipo de Piel</p>
            <p className="text-base font-bold text-[var(--dark)]">{profile.skin_type}</p>
          </div>
          <div className="bg-white/80 rounded-xl p-4">
            <p className="text-[10px] font-semibold text-[var(--violet)] uppercase tracking-wider mb-1">Enfoque Principal</p>
            <p className="text-base font-bold text-[var(--dark)]">{profile.concern}</p>
          </div>
        </div>

        <div className="space-y-3">
          {profile.routine_tip && (
            <div className="bg-white/60 rounded-xl p-4">
              <p className="text-[10px] font-semibold text-[var(--sage-dark)] uppercase tracking-wider mb-1">Tu Rutina Ideal</p>
              <p className="text-sm text-[var(--dark)]/70">{profile.routine_tip}</p>
            </div>
          )}
          {profile.climate_tip && (
            <div className="bg-white/60 rounded-xl p-4">
              <p className="text-[10px] font-semibold text-[var(--sage-dark)] uppercase tracking-wider mb-1">Consejo Para Tu Clima</p>
              <p className="text-sm text-[var(--dark)]/70">{profile.climate_tip}</p>
            </div>
          )}
        </div>
      </div>

      {/* Recommended Products */}
      <div className="mb-6">
        <h3 className="text-lg sm:text-xl font-bold text-[var(--dark)] mb-1">Productos Recomendados Para Ti</h3>
        <p className="text-sm text-[var(--dark)]/40 mb-6">Seleccionados especificamente para tu tipo de piel y necesidades</p>

        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
          {recommended.map(p => {
            const hasDiscount = p.discount_price && p.discount_price < p.price;
            const displayPrice = hasDiscount ? p.discount_price : p.price;
            return (
              <div key={p.id} className="bg-white rounded-2xl overflow-hidden border border-[var(--sage)]/15 hover:shadow-md transition-all group" data-testid={`quiz-product-${p.id}`}>
                <Link to={`/producto/${p.id}`} className="block relative aspect-square overflow-hidden bg-[var(--cream)]">
                  <img src={p.images?.[0]} alt={p.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  {hasDiscount && (
                    <Badge className="absolute top-3 left-3 bg-[var(--violet)] text-white border-0 text-[10px] font-bold px-2 py-0.5 rounded-md">
                      -{Math.round((1 - p.discount_price / p.price) * 100)}%
                    </Badge>
                  )}
                </Link>
                <div className="p-4 space-y-2">
                  <p className="text-[10px] text-[var(--violet)] font-semibold tracking-widest uppercase">{p.brand}</p>
                  <Link to={`/producto/${p.id}`}>
                    <h4 className="text-xs font-semibold text-[var(--dark)] line-clamp-2 hover:text-[var(--violet)] transition-colors min-h-[32px]">{p.name}</h4>
                  </Link>
                  {p.benefits?.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {p.benefits.slice(0, 2).map((b, i) => (
                        <Badge key={i} variant="outline" className="text-[9px] border-[var(--sage)]/30 text-[var(--dark)]/40 px-1.5 py-0">{b}</Badge>
                      ))}
                    </div>
                  )}
                  <div className="flex items-center justify-between pt-1">
                    <div className="flex items-baseline gap-1.5">
                      {hasDiscount && <span className="text-[10px] text-[var(--dark)]/25 line-through">${p.price?.toFixed(2)}</span>}
                      <span className={`text-sm font-bold ${hasDiscount ? 'text-[var(--violet)]' : 'text-[var(--dark)]'}`}>${displayPrice?.toFixed(2)}</span>
                    </div>
                    <Button
                      size="sm"
                      className="h-8 px-3 bg-[var(--dark)] hover:bg-[var(--violet)] text-white rounded-lg text-[10px]"
                      onClick={() => addItem(p)}
                      data-testid={`quiz-add-${p.id}`}
                    >
                      <ShoppingBag className="h-3 w-3 mr-1" /> Agregar
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-col sm:flex-row gap-3 justify-center pt-6">
        <Button
          variant="outline"
          className="rounded-xl border-[var(--sage)]/30 text-sm"
          onClick={onRetake}
          data-testid="quiz-retake-btn"
        >
          <RotateCcw className="mr-2 h-4 w-4" /> Repetir Quiz
        </Button>
        <Link to="/tienda">
          <Button className="rounded-xl bg-[var(--violet)] text-white text-sm px-6" data-testid="quiz-shop-btn">
            Ver Toda la Tienda <ChevronRight className="ml-2 h-4 w-4" />
          </Button>
        </Link>
      </div>
    </div>
  );
}

export default function QuizPage() {
  const [stage, setStage] = useState('intro'); // intro, questions, loading, results
  const [questions, setQuestions] = useState([]);
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState({});
  const [results, setResults] = useState(null);

  const startQuiz = async () => {
    try {
      const res = await axios.get(`${API}/quiz/questions`);
      setQuestions(res.data);
      setStage('questions');
      setCurrentQ(0);
      setAnswers({});
    } catch {
      alert('Error al cargar el quiz');
    }
  };

  const selectAnswer = (optionId) => {
    setAnswers(prev => ({ ...prev, [questions[currentQ].id]: optionId }));
  };

  const nextQuestion = async () => {
    if (currentQ < questions.length - 1) {
      setCurrentQ(prev => prev + 1);
    } else {
      // Submit quiz
      setStage('loading');
      try {
        const res = await axios.post(`${API}/quiz/results`, { answers });
        setResults(res.data);
        setStage('results');
      } catch {
        alert('Error al obtener resultados');
        setStage('questions');
      }
    }
  };

  const prevQuestion = () => {
    if (currentQ > 0) setCurrentQ(prev => prev - 1);
  };

  const retakeQuiz = () => {
    setStage('intro');
    setAnswers({});
    setCurrentQ(0);
    setResults(null);
  };

  return (
    <div className="min-h-screen bg-[var(--cream)]" data-testid="quiz-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {stage === 'intro' && <QuizIntro onStart={startQuiz} />}

        {stage === 'questions' && questions[currentQ] && (
          <QuizQuestion
            question={questions[currentQ]}
            totalQuestions={questions.length}
            currentIndex={currentQ}
            selectedAnswer={answers[questions[currentQ].id]}
            onSelect={selectAnswer}
            onNext={nextQuestion}
            onBack={prevQuestion}
          />
        )}

        {stage === 'loading' && (
          <div className="flex flex-col items-center justify-center py-20 gap-4" data-testid="quiz-loading">
            <Loader2 className="h-10 w-10 text-[var(--violet)] animate-spin" />
            <p className="text-sm text-[var(--dark)]/50">Analizando tu perfil de piel...</p>
          </div>
        )}

        {stage === 'results' && results && (
          <QuizResults
            profile={results.profile}
            recommended={results.recommended}
            onRetake={retakeQuiz}
          />
        )}
      </div>
    </div>
  );
}
